import { collection, limit, query, where } from "firebase/firestore";
import { useMemo } from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../services/Firebase";
import { useWorkspaceConnections } from "./useWorkspaceConnections";
import { useSetupPillDismissal } from "./useSetupPillDismissal";

export type OnboardingStep = "generate" | "brandKit" | "connect" | "autopilot";

export interface OnboardingProgress {
  steps: Record<OnboardingStep, boolean>;
  doneCount: number;
  allDone: boolean;
  nextStep: OnboardingStep | null;
  dismissed: boolean;
  dismiss: () => void;
  loading: boolean;
}

const ZERO_PROGRESS: OnboardingProgress = {
  steps: { generate: false, brandKit: false, connect: false, autopilot: false },
  doneCount: 0,
  allDone: false,
  nextStep: "brandKit",
  dismissed: false,
  dismiss: () => {},
  loading: true,
};

const STEP_ORDER: OnboardingStep[] = [
  "brandKit",
  "connect",
  "generate",
  "autopilot",
];

export function useOnboardingProgress(): OnboardingProgress {
  const { activeWorkspaceId } = useAuth();
  const uid = activeWorkspaceId;
  const { dismissed, dismiss } = useSetupPillDismissal();

  const [postsSnap, postsLoading] = useCollection(
    uid
      ? query(collection(db, "posts"), where("userId", "==", uid), limit(1))
      : null
  );
  const [brandKitsSnap, brandKitsLoading] = useCollection(
    uid
      ? query(
          collection(db, "brand_kits"),
          where("userId", "==", uid),
          limit(1)
        )
      : null
  );
  const { connections, loading: connectionsLoading } =
    useWorkspaceConnections();
  const [sourcesSnap, sourcesLoading] = useCollection(
    uid
      ? query(
          collection(db, "monitoring_sources"),
          where("userId", "==", uid),
          limit(1)
        )
      : null
  );

  return useMemo<OnboardingProgress>(() => {
    if (!uid) return ZERO_PROGRESS;

    const loading =
      postsLoading ||
      brandKitsLoading ||
      connectionsLoading ||
      sourcesLoading;

    const steps: Record<OnboardingStep, boolean> = {
      generate: postsSnap ? !postsSnap.empty : false,
      brandKit: brandKitsSnap ? !brandKitsSnap.empty : false,
      connect: connections.length > 0,
      autopilot: sourcesSnap ? !sourcesSnap.empty : false,
    };

    const doneCount = Object.values(steps).filter(Boolean).length;
    const allDone = doneCount === STEP_ORDER.length;
    const nextStep = STEP_ORDER.find((s) => !steps[s]) ?? null;

    return { steps, doneCount, allDone, nextStep, dismissed, dismiss, loading };
  }, [
    uid,
    dismissed,
    dismiss,
    postsSnap,
    brandKitsSnap,
    connections,
    sourcesSnap,
    postsLoading,
    brandKitsLoading,
    connectionsLoading,
    sourcesLoading,
  ]);
}
